import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  RELATION_BUILD_DEPENDENCY_OF,
  RELATION_DEPLOY_DEPENDENCY_OF,
  RELATION_DEPLOY_DEPENDS_ON,
  RELATION_RUNTIME_DEPENDENCY_OF,
  RELATION_RUNTIME_DEPENDS_ON,
} from 'backstage-plugin-neo4j-software-catalogue-common';
import ArrowForwardRounded from '@material-ui/icons/ArrowForwardRounded';
import KeyboardArrowRightRounded from '@material-ui/icons/KeyboardArrowRightRounded';
import {
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CardHeader from '@material-ui/core/CardHeader';
import { EntityRefLink } from '../EntityRefLink';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

const useStyles = makeStyles(theme => ({
  cardHeader: {
    marginBottom: theme.spacing(2),
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    minHeight: 200,
  },
  rootEntityChip: {
    transform: 'translateY(3px)',
  },
  fullGraphLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 1, 1, 1.5),
    textDecoration: 'none',
  },
  relationArrow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  noPadding: {
    padding: 0,
  },
  noBullets: {
    listStyle: 'none',
  },
  dependencyList: {
    display: 'block',
    marginTop: 'unset',
  },
  twoColumn: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  column: {
    flex: '1 1 0',
    padding: 0,
    margin: 0,
  },
  groupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

type DependencyRef = {
  entityRef: string;
  entityName: string;
};

type DependencyRelationGroup = {
  title: string;
  explanation: string;
  items: DependencyRef[];
};

type RelationGroupMap = Record<string, DependencyRelationGroup>;

type DependencySides = {
  dependents: RelationGroupMap;
  dependencies: RelationGroupMap;
};

type Styles = ReturnType<typeof useStyles>;

export type DependencyCardProps = {
  entity: Entity;
};

export const DependencyCard = (props: DependencyCardProps) => {
  const classes = useStyles();
  const entity = props.entity;

  const entityName = entity?.metadata?.name || stringifyEntityRef(entity);
  const isLibrary = entity.spec?.type === 'library';

  const groupedDependencies: DependencySides = {
    dependents: {
      // Build dependents only shown for libraries - shows which components use this library
      ...(isLibrary && {
        [RELATION_BUILD_DEPENDENCY_OF]: {
          title: 'Build',
          explanation: `These depend on ${entityName} to build.`,
          items: [],
        },
      }),
      [RELATION_DEPLOY_DEPENDENCY_OF]: {
        title: 'Deploy',
        explanation: `These depend on ${entityName} to be able to deploy.`,
        items: [],
      },
      [RELATION_RUNTIME_DEPENDENCY_OF]: {
        title: 'Runtime',
        explanation: `These depend on ${entityName} to be able to run correctly.`,
        items: [],
      },
    },
    dependencies: {
      [RELATION_DEPLOY_DEPENDS_ON]: {
        title: 'Deploy',
        explanation: `${entityName} depends on these to be able to deploy.`,
        items: [],
      },
      [RELATION_RUNTIME_DEPENDS_ON]: {
        title: 'Runtime',
        explanation: `${entityName} depends on these to be able to run correctly.`,
        items: [],
      },
    },
  };

  for (const rel of entity.relations || []) {
    const depItem: DependencyRef = {
      entityRef: rel.targetRef,
      entityName: parseEntityRef(rel.targetRef).name,
    };

    if (rel.type in groupedDependencies.dependents) {
      groupedDependencies.dependents[rel.type].items.push(depItem);
    } else if (rel.type in groupedDependencies.dependencies) {
      groupedDependencies.dependencies[rel.type].items.push(depItem);
    }
  }

  // Sort items alphabetically by entityName
  (['dependents', 'dependencies'] as Array<keyof DependencySides>).forEach(
    sideKey => {
      const side = groupedDependencies[sideKey];
      Object.keys(side).forEach(relationType => {
        side[relationType].items.sort((a, b) =>
          a.entityName.localeCompare(b.entityName),
        );
      });
    },
  );

  const noDependenciesAtAll =
    !sideHasItems(groupedDependencies.dependents) &&
    !sideHasItems(groupedDependencies.dependencies);

  return (
    <Card>
      <CardHeader title="Dependencies" className={classes.cardHeader} />
      <Divider />
      <CardContent className={classes.cardContent}>
        <DependencySide
          side={groupedDependencies.dependents}
          classes={classes}
          entity={entity}
          arrowOnLeft={false}
        />

        {noDependenciesAtAll ? (
          <Typography>No dependencies</Typography>
        ) : (
          <Chip label={entityName} className={classes.rootEntityChip} />
        )}

        <DependencySide
          side={groupedDependencies.dependencies}
          classes={classes}
          entity={entity}
          arrowOnLeft
        />
      </CardContent>
      <Divider />
      <CardActions>
        <Typography
          component="a"
          href={getGraphUrl(entity)}
          variant="body1"
          color="textSecondary"
          className={classes.fullGraphLink}
        >
          <strong>View full graph</strong>
          <ArrowForwardRounded fontSize="small" />
        </Typography>
      </CardActions>
    </Card>
  );
};

function DependencySide(props: {
  side: RelationGroupMap;
  classes: Styles;
  entity: Entity;
  arrowOnLeft: boolean;
}) {
  if (!sideHasItems(props.side)) {
    return null;
  }

  const relationArrow = (
    <KeyboardArrowRightRounded fontSize="medium" color="primary" />
  );

  return (
    <>
      {props.arrowOnLeft ? relationArrow : null}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {Object.keys(props.side).map(relationType => (
          <DependencyList
            key={relationType}
            dependencyGroup={props.side[relationType]}
            classes={props.classes}
          />
        ))}
      </div>
      {!props.arrowOnLeft ? relationArrow : null}
    </>
  );
}

function DependencyList({
  classes,
  dependencyGroup,
}: {
  classes: Styles;
  dependencyGroup: DependencyRelationGroup;
}) {
  const items = dependencyGroup.items;

  if (items.length === 0) {
    return null;
  }

  const isTwoColumn = items.length > 20;

  const title = (
    <Tooltip
      title={dependencyGroup.explanation}
      placement="top"
      aria-label={dependencyGroup.explanation}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
        className={classes.groupTitle}
      >
        <strong>{dependencyGroup.title}</strong>
        <InfoOutlined fontSize="inherit" />
      </Typography>
    </Tooltip>
  );

  if (!isTwoColumn) {
    return (
      <div>
        {title}
        <ul className={`${classes.dependencyList} ${classes.noPadding}`}>
          {items.map(dependency => (
            <DependencyListItem
              key={dependency.entityRef}
              dependency={dependency}
              classes={classes}
            />
          ))}
        </ul>
      </div>
    );
  }

  const mid = Math.ceil(items.length / 2);
  const leftColumn = items.slice(0, mid);
  const rightColumn = items.slice(mid);

  return (
    <div>
      {title}
      <div className={`${classes.dependencyList} ${classes.twoColumn}`}>
        <ul className={`${classes.column} ${classes.noPadding}`}>
          {leftColumn.map(dependency => (
            <DependencyListItem
              key={dependency.entityRef}
              dependency={dependency}
              classes={classes}
            />
          ))}
        </ul>
        <ul className={`${classes.column} ${classes.noPadding}`}>
          {rightColumn.map(dependency => (
            <DependencyListItem
              key={dependency.entityRef}
              dependency={dependency}
              classes={classes}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function DependencyListItem({
  dependency,
  classes,
}: {
  dependency: DependencyRef;
  classes: Styles;
}) {
  return (
    <li className={classes.noBullets}>
      <EntityRefLink entityRef={parseEntityRef(dependency.entityRef)} />
    </li>
  );
}

function getGraphUrl(entity: Entity) {
  const selectedRelations = [
    'partOf',
    'hasPart',
    'apiConsumedBy',
    'apiProvidedBy',
    'consumesApi',
    'providesApi',
    'dependencyOf',
    'dependsOn',
    'buildDependsOn',
    'buildDependencyOf',
    'runtimeDependsOn',
    'runtimeDependencyOf',
    'deployDependsOn',
    'deployDependencyOf',
  ];

  const params = new URLSearchParams();
  params.append('rootEntityRefs[]', stringifyEntityRef(entity));
  params.set('maxDepth', '1');
  params.set('unidirectional', 'true');
  params.set('mergeRelations', 'false');
  selectedRelations.forEach(r => params.append('selectedRelations[]', r));
  params.set('direction', 'LR');

  return `/catalog-graph?${params.toString()}`;
}

function sideHasItems(side: RelationGroupMap) {
  return Object.values(side).some(group => group.items.length > 0);
}
